import React from 'react';
import { X as CloseIcon, Check as CheckIcon, Circle as FiberManualRecordIcon } from 'lucide-react';

// Helper function to format date objects safely into readable strings
const formatDateString = (dateIsoStr) => {
    if (!dateIsoStr) return '';
    const dateObj = new Date(dateIsoStr);
    return dateObj.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    }); // Outputs: DD-MM-YYYY
};

const formatTextDate = (dateIsoStr) => {
    if (!dateIsoStr) return '';
    const dateObj = new Date(dateIsoStr);
    // Match format: 12th Jul, 2026, 11:59 PM
    const day = dateObj.getDate();
    const suffix = ["th", "st", "nd", "rd"][((day % 100) - 20) % 10] || ["th", "st", "nd", "rd"][day % 10] || "th";
    const month = dateObj.toLocaleString('en-US', { month: 'short' });
    const year = dateObj.getFullYear();
    let hours = dateObj.getHours();
    const minutes = dateObj.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;

    return `${day}${suffix} ${month}, ${year}, ${hours}:${minutes} ${ampm}`;
};

export default function CancellationPolicyModal({ isOpen, onClose, cancelArr = [] }) {
    if (!isOpen) return null;

    // Derive target timelines from the provided dynamic array payload structure
    const tier1 = cancelArr[0]; // 100% Refund zone
    const tier2 = cancelArr[1]; // Partial penalty zone
    const tier3 = cancelArr[2]; // Fully non-refundable zone

    const currencySymbol = tier1?.Currency === 'INR' || !tier1?.Currency ? '₹' : tier1.Currency;
    const firstTier = cancelArr[0];
    const lastTier = cancelArr[cancelArr.length - 1];
    return (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">

            {/* MODAL MAIN DIALOG CONTAINER */}
            <div className="relative w-full max-w-4xl bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[90vh]">

                {/* HEADER SECTION */}
                <div className="p-3 border-b border-slate-100 flex items-center justify-between">
                    <h2 className="text-xl font-bold text-slate-900 tracking-tight">
                        Room with Cancellation Policy
                    </h2>
                    <button onClick={onClose}
                        className="close w-8 h-8 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all"
                    >
                        <CloseIcon fontSize="small" />
                    </button>
                </div>

                {/* CONTENT VIEWPORT SCROLL CONTAINER */}
                <div className="p-6 overflow-y-auto space-y-6 text-sm text-slate-700">

                    {/* BULLET SUMMARY RECAP */}
                    <div className="space-y-2">
                        <h3 className="font-bold text-slate-950">Cancellation Policy :</h3>
                        <ul className="space-y-1.5 font-medium text-slate-700">
                            <li className="flex items-start gap-2 text-slate-800">
                                <CheckIcon className="text-amber-500 mt-0.5 scale-90" fontSize="small" />
                                <span>Free Cancellation till {formatTextDate(tier1?.todate)}</span>
                            </li>
                            <li className="flex items-start gap-2 text-slate-800">
                                <CheckIcon className="text-amber-500 mt-0.5 scale-90" fontSize="small" />
                                <span>No Refund if canceled after {formatTextDate(tier2?.todate)}</span>
                            </li>
                        </ul>
                    </div>

                    {/* DYNAMIC TIMELINE VISUAL ELEMENT */}
                    <div className="">
                        <div className="w-full flex h-9 rounded-full overflow-hidden shadow-sm border border-slate-200">

                            {cancelArr.map((tier, index) => {
                                const isFirst = index === 0;
                                const isLast = index === cancelArr.length - 1;

                                let bg = "#fbf3d5";
                                let text = "text-amber-900";
                                let label = "";

                                if (isFirst) {
                                    bg = "#5ba199";
                                    text = "text-white";
                                    label = "100% Refund";
                                }

                                if (isLast) {
                                    bg = "#de5454";
                                    text = "text-white";
                                    label = "Non-Refundable";
                                }

                                return (
                                    <div
                                        key={index}
                                        className={`${text} flex-1 flex items-center justify-center text-xs font-bold border-r last:border-r-0 border-dashed border-slate-700/30`}
                                        style={{ backgroundColor: bg }}
                                    >
                                        {label}
                                    </div>
                                );
                            })}
                        </div>

                        <div
                            className="mt-2 text-[11px] font-bold text-slate-600"
                            style={{
                                display: "grid",
                                gridTemplateColumns: `repeat(${cancelArr.length + 1}, minmax(0,1fr))`
                            }}
                        >
                            <div className="text-left">Now</div>

                            {cancelArr.map((tier, index) => (
                                <div
                                    key={index}
                                    className={index === cancelArr.length - 1 ? "text-right" : "text-center"}
                                >
                                    <div>
                                        {formatTextDate(tier.todate).split(",")[0]}{" "}
                                        {formatTextDate(tier.todate).split(",")[2]}
                                    </div>

                                    {index === cancelArr.length - 1 && (
                                        <div className="text-slate-900 text-xs font-extrabold uppercase">
                                            Check-In
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* CALLOUT DESCRIPTION TEXT */}
                    <div className="flex items-start gap-2 text-slate-600">
                        <FiberManualRecordIcon className="text-slate-400 mt-1.5 shrink-0 scale-50" style={{ fontSize: 10 }} />
                        <p>
                            Free Cancellation (100% refund) if you cancel this booking before <span className="font-semibold text-slate-800">{formatTextDate(tier1?.todate)}</span>
                        </p>
                    </div>

                    {/* PENALTY CHARGES MATRIX TABLE */}
                    <div className="space-y-3">
                        <h4 className="font-bold text-slate-900">
                            Cancellation post that will be subject to a fees as follows
                        </h4>

                        <div className="border border-slate-200 rounded-lg overflow-hidden bg-white shadow-sm">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-slate-50 text-slate-600 font-semibold text-xs border-b border-slate-200">
                                        <th className="p-3 text-center">Cancellation On or After</th>
                                        <th className="p-3 text-center">Cancellation On or Before</th>
                                        <th className="p-3 text-center">Cancellation Charges/Comments</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 text-center text-slate-700 font-medium">
                                    {cancelArr.map((tier, idx) => (
                                        <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                                            <td className="p-3.5 text-slate-600">{formatDateString(tier.fromddate)}</td>
                                            <td className="p-3.5 text-slate-600">{formatDateString(tier.todate)}</td>
                                            <td className="p-3.5 font-bold text-slate-900">
                                                {tier.am === 0 ? (
                                                    <span className="text-emerald-600">{currencySymbol} 0</span>
                                                ) : (
                                                    <span>{currencySymbol} {tier.am.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}

                                    {/* FOOTER STATUTORY LEGAL DISCLAIMERS */}
                                    <tr className="bg-slate-50/40">
                                        <td colSpan="3" className="p-2 text-slate-500 font-normal text-xs text-center border-t border-slate-200">
                                            No Show will attract full cancellation charge unless otherwise specified.
                                        </td>
                                    </tr>
                                    <tr className="bg-slate-50/40">
                                        <td colSpan="3" className="p-2 text-slate-500 font-normal text-xs text-center">
                                            Early check out will attract full cancellation charge unless otherwise specified.
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                </div>

            </div>
        </div>
    );
}